import DOMPurify from 'dompurify';

const LRI = '\u2066';
const RLI = '\u2067';
const PDI = '\u2069';

const protectParensBiDi = (s) => {
  const wrap = (open, close, test, dir) =>
    s.replace(
      new RegExp(`\\${open}([^\\${close}]*)\\${close}`, 'g'),
      (m, inner) => {
        if (inner.includes(LRI) || inner.includes(RLI) || inner.includes(PDI))
          return m;
        const dirAttr = dir === '\u2067' ? 'rtl' : 'ltr';
        return test.test(inner)
          ? `<bdi dir="${dirAttr}">${open}${inner}${close}</bdi>`
          : m;
      }
    );

  // Arabic (incl. Hebrew + Arabic digits) → RTL isolate
  const AR =
    /[\u0590-\u05FF\u0600-\u06FF\u0750-\u08FF\u0660-\u0669\u06F0-\u06F9]/;

  s = wrap('(', ')', AR, RLI);
  s = wrap('[', ']', AR, RLI);
  s = wrap('{', '}', AR, RLI);

  // Latin → LTR isolate
  s = wrap('(', ')', /[A-Za-z]/, LRI);
  s = wrap('[', ']', /[A-Za-z]/, LRI);
  s = wrap('{', '}', /[A-Za-z]/, LRI);

  return s;
};
function convertMarkdownToHTML(input) {
  if (!input) return '';
  const applyInlineFormatting = (t) => {
    // === BiDi MUST run BEFORE any HTML injection ===
    t = protectParensBiDi(t);

    // Bold+italic (triple)
    t = t.replace(/\*\*\*([^*]+)\*\*\*/g, '<strong><em>$1</em></strong>');
    t = t.replace(/___([^_]+)___/g, '<strong><em>$1</em></strong>');

    // Bold
    t = t.replace(/\*\*(?!\*)(.+?)\*\*(?!\*)/g, '<strong>$1</strong>');
    t = t.replace(/__(?!_)(.+?)__(?!_)/g, '<strong>$1</strong>');

    // Italic
    t = t.replace(/(?<!\*)\*(?!\*)([^*]+)\*(?!\*)/g, '<em>$1</em>');
    t = t.replace(/(?<!_)_(?!_)([^_]+)_(?!_)/g, '<em>$1</em>');

    // Strikethrough
    t = t.replace(/~~(.+?)~~/g, '<s>$1</s>');

    // Inline code
    t = t.replace(/`([^`]+?)`/g, (_m, codeText) => {
      const esc = codeText
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return `<code>${esc}</code>`;
    });

    // Links [text](url)
    t = t.replace(/\[([^\]]+)\]\((https?:\/\/.+?)\)/g, (_m, text, url) => {
      const encodedUrl = String(url).trim().replace(/ /g, '%20');
      return `<a href="${encodedUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    });

    // Plain URLs
    t = t.replace(/(?<!["'(\]])\bhttps?:\/\/[^\s<]+/g, (url) => {
      const encodedUrl = url.replace(/ /g, '%20');
      return `<a href="${encodedUrl}" target="_blank" rel="noopener noreferrer">${encodedUrl}</a>`;
    });

    // Emails
    t = t.replace(
      /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z]{2,}\b/gi,
      (email) =>
        `<a href="mailto:${email}" target="_top" rel="noopener">${email}</a>`
    );

    return t;
  };

  // --- Normalize input to simplify parsing ---
  let text = String(input);
  text = text.replace(/[\u2066\u2067\u2068\u2069]/g, '');

  // 1. Ensure "Step X:" starts on a new line (for ordered list detection)
  text = text.replace(/(Step \d+:)/g, '\n$1');
  // 2. Ensure "- Something:" (list with colon) starts on new line (avoid being parsed as one paragraph)
  text = text.replace(/- ([^:\n]+):/g, '\n- $1:');
  text = text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\r\n?/g, '\n');
  // 3. Insert spaces in camelCase words for better break-word handling, but protect URLs/emails
  text = text
    .split(/(\bhttps?:\/\/\S+|\S+@\S+\.\S+)/g)
    .map((chunk) =>
      /@|:\/\//.test(chunk) ? chunk : chunk.replace(/([a-z])([A-Z])/g, '$1 $2')
    )
    .join('');

  const lines = text.split('\n');
  // --- TABLE HELPERS ---
  const stripOuterPipes = (s) =>
    s
      .trim()
      .replace(/^\|\s*/, '')
      .replace(/\s*\|$/, '');
  const splitRow = (s) =>
    stripOuterPipes(s)
      .split('|')
      .map((c) => c.trim());

  const isTableRowLine = (s) => {
    const t = s.trim();
    if (!t.includes('|')) return false;
    const cells = splitRow(t);
    // must have at least 2 cells and not be a horizontal rule
    return cells.length >= 2 && !/^(\*{3,}|-{3,}|_{3,})\s*$/.test(t);
  };

  const isSeparatorLine = (s) => {
    const t = stripOuterPipes(s)
      .split('|')
      .map((c) => c.trim());
    if (t.length < 2) return false;
    // ---  :---  ---:  :---:
    return t.every((c) => /^:?-{3,}:?$/.test(c));
  };

  const alignFromSepCell = (cell) => {
    const left = cell.trim().startsWith(':');
    const right = cell.trim().endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    return 'left';
  };
  // --- State variables for list and block handling ---
  let html = '';
  let inOL = false; // currently inside an <ol>
  let inTopUL = false; // currently inside a top-level <ul>
  let inNestedUL = false; // currently inside a nested <ul> within a list item
  let liOpen = false; // currently within an open <li>
  let inBlockquote = false; // currently inside a <blockquote>
  let inCodeBlock = false; // currently inside a ``` code block
  let codeBlockContent = ''; // accumulator for code block content

  for (let idx = 0; idx < lines.length; idx++) {
    const raw = lines[idx];
    const indent = raw.search(/\S|$/);
    const line = raw.trim();

    if (!line) {
      if (inCodeBlock) {
        codeBlockContent += '\n'; // preserve blank lines in code blocks
      }
      continue;
    }

    // 1. Horizontal rule (---, ***, ___ on a line by itself)
    if (/^(\*{3,}|-{3,}|_{3,})\s*$/.test(line)) {
      // Close any open list or blockquote before adding <hr/>
      if (inNestedUL) {
        html += '</ul>';
        inNestedUL = false;
      }
      if (liOpen) {
        html += '</li>';
        liOpen = false;
      }
      if (inOL) {
        html += '</ol>';
        inOL = false;
      }
      if (inTopUL) {
        html += '</ul>';
        inTopUL = false;
      }
      if (inBlockquote) {
        html += '</blockquote>';
        inBlockquote = false;
      }
      html += '<hr />';
      continue;
    }

    // 2. Fenced Code Block (```...```)
    if (line.startsWith('```')) {
      if (!inCodeBlock) {
        // Start of code block
        inCodeBlock = true;
        codeBlockContent = '';
        continue;
      } else {
        // End of code block – output accumulated code
        inCodeBlock = false;
        const escapedCode = codeBlockContent
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        html += `<pre><code>${escapedCode}</code></pre>`;
        codeBlockContent = '';
        continue;
      }
    }
    if (inCodeBlock) {
      // Inside code block, accumulate raw lines (preserve indentation and all content)
      codeBlockContent += (codeBlockContent ? '\n' : '') + raw;
      continue;
    }

    // 3. Blockquote
    if (line.startsWith('>')) {
      const quoteContent = line.slice(1).trim(); // remove leading '>'
      if (!inBlockquote) {
        // Entering a new blockquote: close open lists if any
        if (liOpen) {
          if (inNestedUL) {
            html += '</ul>';
            inNestedUL = false;
          }
          html += '</li>';
          liOpen = false;
        }
        if (inOL) {
          html += '</ol>';
          inOL = false;
        }
        if (inTopUL) {
          html += '</ul>';
          inTopUL = false;
        }
        html += '<blockquote>';
        inBlockquote = true;
      }
      // Each quoted line becomes its own paragraph in the blockquote
      html += `<p>${applyInlineFormatting(quoteContent)}</p>`;
      continue;
    } else {
      // If we were in a blockquote and this line is not quoted, close the blockquote
      if (inBlockquote) {
        html += '</blockquote>';
        inBlockquote = false;
      }
    }

    // 4. Headings (lines starting with #)
    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      // Close any open list item or list before starting a heading
      if (inNestedUL) {
        html += '</ul>';
        inNestedUL = false;
      }
      if (liOpen) {
        html += '</li>';
        liOpen = false;
      }
      if (inOL) {
        html += '</ol>';
        inOL = false;
      }
      if (inTopUL) {
        html += '</ul>';
        inTopUL = false;
      }
      const level = headingMatch[1].length;
      html += `<h${level}>${applyInlineFormatting(
        headingMatch[2]
      )}</h${level}>`;
      continue;
    }
    // ======================
    //      TABLE BLOCK
    // ======================
    if (
      isTableRowLine(line) &&
      idx + 1 < lines.length &&
      isSeparatorLine(lines[idx + 1])
    ) {
      // Close any open list/quote context
      if (inNestedUL) {
        html += '</ul>';
        inNestedUL = false;
      }
      if (liOpen) {
        html += '</li>';
        liOpen = false;
      }
      if (inOL) {
        html += '</ol>';
        inOL = false;
      }
      if (inTopUL) {
        html += '</ul>';
        inTopUL = false;
      }

      const headerCells = splitRow(line);
      const sepCells = stripOuterPipes(lines[idx + 1])
        .split('|')
        .map((c) => c.trim());
      const aligns = sepCells.map(alignFromSepCell);

      let bodyRows = [];
      idx += 2; // skip header + separator

      while (idx < lines.length && isTableRowLine(lines[idx])) {
        bodyRows.push(splitRow(lines[idx]));
        idx++;
      }
      idx--; // outer for-loop will ++

      const colCount = Math.max(
        headerCells.length,
        aligns.length,
        ...bodyRows.map((r) => r.length)
      );

      const ths = Array.from({ length: colCount })
        .map((_, i) => {
          const txt = applyInlineFormatting(headerCells[i] ?? '');
          const align = aligns[i] ?? 'left';
          return `<th align="${align}">${txt}</th>`;
        })
        .join('');

      const trs = bodyRows
        .map((r) => {
          const tds = Array.from({ length: colCount })
            .map((_, i) => {
              const txt = applyInlineFormatting(r[i] ?? '');
              const align = aligns[i] ?? 'left';
              return `<td align="${align}">${txt}</td>`;
            })
            .join('');
          return `<tr>${tds}</tr>`;
        })
        .join('');

      html += `<table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`;
      continue;
    }

    // 5. "Step X:" pattern (treated as an ordered list item)
    if (/^Step\s+\d+:/i.test(line)) {
      const itemText = line.replace(/^Step\s+\d+:\s*/, '');
      if (!inOL) {
        // If not already in an OL, close any UL and open a new OL
        if (inTopUL) {
          if (liOpen) {
            if (inNestedUL) {
              html += '</ul>';
              inNestedUL = false;
            }
            html += '</li>';
            liOpen = false;
          }
          html += '</ul>';
          inTopUL = false;
        }
        html += '<ol>';
        inOL = true;
      }
      // Close previous list item if still open
      if (liOpen) {
        if (inNestedUL) {
          html += '</ul>';
          inNestedUL = false;
        }
        html += '</li>';
        liOpen = false;
      }
      // Start a new list item for this "Step"
      html += `<li>${applyInlineFormatting(itemText)}`;
      liOpen = true;
      continue;
    }

    // 6. Numbered list item (e.g. "1. text" or "1) text")
    if (/^\d+[\.\)]\s+/.test(line)) {
      const itemText = line.replace(/^\d+[\.\)]\s+/, '');
      if (!inOL) {
        // If currently in a UL, close it and start a new OL
        if (inTopUL) {
          if (liOpen) {
            if (inNestedUL) {
              html += '</ul>';
              inNestedUL = false;
            }
            html += '</li>';
            liOpen = false;
          }
          html += '</ul>';
          inTopUL = false;
        }
        html += '<ol>';
        inOL = true;
      }
      // If a list item is open, close it (end of previous item)
      if (liOpen) {
        if (inNestedUL) {
          html += '</ul>';
          inNestedUL = false;
        }
        html += '</li>';
        liOpen = false;
      }
      // Start a new <li> for this numbered item
      html += `<li>${applyInlineFormatting(itemText)}`;
      liOpen = true;
      continue;
    }

    // 7. Bullet list item (unordered list, e.g. "- text" or "* text")
    if (/^[\-\*\u2022\u2013\u2014]\s+/.test(line)) {
      const itemText = line.replace(/^[\-\*\u2022\u2013\u2014]\s+/, '');
      // If currently inside an OL (numbered list) item, treat this as a nested UL
      if (inOL && liOpen) {
        if (!inNestedUL) {
          html += '<ul>'; // open a new nested UL inside the OL <li>
          inNestedUL = true;
        }
        html += `<li>${applyInlineFormatting(itemText)}</li>`;
        continue;
      }
      // Otherwise, handle as a normal bullet list item
      if (!inTopUL) {
        // If not already in a UL, close any open OL and start a new UL
        if (inOL) {
          if (liOpen) {
            if (inNestedUL) {
              html += '</ul>';
              inNestedUL = false;
            }
            html += '</li>';
            liOpen = false;
          }
          html += '</ol>';
          inOL = false;
        }
        html += '<ul>';
        inTopUL = true;
      }
      // Check indent to determine nested bullet under a parent bullet
      if (indent >= 2 && liOpen) {
        // Indented bullet -> nested UL inside current list item
        if (!inNestedUL) {
          html += '<ul>';
          inNestedUL = true;
        }
        html += `<li>${applyInlineFormatting(itemText)}</li>`;
        continue;
      }
      // If a list item (bullet or step) is open and this bullet is same level, close previous item
      if (liOpen) {
        if (inNestedUL) {
          html += '</ul>';
          inNestedUL = false;
        }
        html += '</li>';
        liOpen = false;
      }
      // Open a new bullet list item
      html += `<li>${applyInlineFormatting(itemText)}`;
      liOpen = true;
      continue;
    }

    // 8. Plain paragraph text (not a special markdown line)
    const isListMarker =
      /^(\d+[\.\)]\s+|Step\s+\d+:|[\-\*\u2022\u2013\u2014]\s+)/i.test(line);
    if (liOpen && !isListMarker && indent === 0) {
      if (inNestedUL) {
        html += '</ul>';
        inNestedUL = false;
      }
      html += '</li>';
      liOpen = false;
      if (inOL) {
        html += '</ol>';
        inOL = false;
      }
      if (inTopUL) {
        html += '</ul>';
        inTopUL = false;
      }
    }

    html += `<p>${applyInlineFormatting(line)}</p>`;
  }

  // --- Close any tags still open at end of input ---
  if (inCodeBlock) {
    const escapedCode = codeBlockContent
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    html += `<pre><code>${escapedCode}</code></pre>`;
    inCodeBlock = false;
  }
  if (inBlockquote) {
    html += '</blockquote>';
    inBlockquote = false;
  }
  if (liOpen) {
    if (inNestedUL) {
      html += '</ul>';
      inNestedUL = false;
    }
    html += '</li>';
    liOpen = false;
  }
  if (inOL) {
    html += '</ol>';
    inOL = false;
  }
  if (inTopUL) {
    html += '</ul>';
    inTopUL = false;
  }

  // Sanitize the generated HTML to allow only safe tags and attributes
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'a',
      'strong',
      'em',
      'p',
      'ul',
      'ol',
      'li',
      'br',
      'span',
      'div',
      'b',
      'i',
      'u',
      'code',
      'pre',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'bdi',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'align',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'dir', 'class'],
    ALLOWED_URI_REGEXP:
      /^(?:(?:https?|mailto|tel|sms):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  });
}

export { convertMarkdownToHTML };
